import { api } from '../lib/api'

export type CommentTargetType = 'post' | 'person' | 'listing' | 'collection'

export type CommentAuthor = {
  id: string | number
  name: string
  handle?: string
  avatar_url?: string | null
}

export type CommentDTO = {
  id: string | number
  author: CommentAuthor
  author_id?: string | number
  body: string
  body_plain?: string | null
  body_html?: string | null
  created_at: string
  parent_id: string | number | null
  reactions?: Record<string, number>
  my_reaction?: string | null
  gift_count?: number
  gift_value_cents?: number
}

export type ListCommentsResponse = {
  results: CommentDTO[]
  next?: string | null
  previous?: string | null
}

const TARGET_FIELD_MAP: Record<CommentTargetType, string> = {
  post: 'post_id',
  person: 'profile_id',
  listing: 'listing_id',
  collection: 'collection_id',
}

function encodeTarget(type: CommentTargetType, id: string | number) {
  const field = TARGET_FIELD_MAP[type] ?? 'target_id'
  return { field, value: String(id) }
}

function buildAuthor(raw: any): CommentAuthor {
  if (raw?.author) {
    return {
      id: raw.author.id ?? raw.author_id ?? 'unknown',
      name: raw.author.name || 'Anonymous',
      handle: raw.author.handle || '',
      avatar_url: raw.author.avatar_url ?? null,
    }
  }
  return {
    id: raw?.author_id ?? 'unknown',
    name: raw?.author_name || 'Anonymous',
    handle: raw?.author_handle || '',
    avatar_url: raw?.author_avatar_url ?? null,
  }
}

function normalizeComment(raw: any): CommentDTO {
  const author = buildAuthor(raw)
  const bodyPlain = raw.body_plain ?? raw.body ?? ''
  const reactions =
    raw.reactions ?? raw.reactions_breakdown ?? (raw.likes_count != null ? { like: raw.likes_count } : undefined)
  return {
    id: raw.id,
    author,
    author_id: raw.author_id ?? author.id,
    body: bodyPlain,
    body_plain: bodyPlain,
    body_html: raw.body_html ?? null,
    created_at: raw.created_at ?? new Date().toISOString(),
    parent_id: raw.parent ?? null,
    reactions,
    my_reaction: raw.my_reaction ?? null,
    gift_count: raw.gift_count ?? 0,
    gift_value_cents: raw.gift_value_cents ?? 0,
  }
}

export async function listComments(params: {
  type: CommentTargetType
  id: string | number
  cursor?: string | null
  sort?: 'top' | 'newest' | 'following'
}) {
  // Current backend list endpoint supports only post_id.
  // Avoid firing unsupported profile/listing/collection requests that 400.
  if (params.type !== 'post') {
    return {
      items: [] as CommentDTO[],
      next_cursor: null as string | null,
    }
  }
  const search = new URLSearchParams()
  const target = encodeTarget(params.type, params.id)
  search.set(target.field, target.value)
  if (params.cursor) search.set('cursor', params.cursor)
  if (params.sort) search.set('tab', params.sort)
  const { data } = await api.get<ListCommentsResponse>(`/api/comments/?${search.toString()}`)
  const raw = Array.isArray(data.results) ? data.results : []
  return {
    items: raw.map(normalizeComment),
    next_cursor: (data as any)?.next ?? null,
  }
}

export async function createComment(params: {
  type: CommentTargetType
  id: string | number
  body: string
  parent_id?: string | number | null
}) {
  const target = encodeTarget(params.type, params.id)
  const payload: Record<string, any> = { body_markdown: params.body }
  payload[target.field] = target.value
  if (params.parent_id != null) payload.parent = params.parent_id
  const { data } = await api.post(`/api/comments/`, payload)
  return normalizeComment(data)
}

export type CommentReaction = 'like' | 'thumbsup' | 'thumbsdown'

export async function reactToComment(params: { comment_id: string | number; reaction: CommentReaction }) {
  const { data } = await api.post<{ ok: boolean; my_reaction?: CommentReaction | null; counts?: Record<string, number> }>(
    `/api/comments/${params.comment_id}/react/`,
    { emoji: params.reaction }
  )
  return data
}
